import { useMemo } from 'react';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getEntryCollection } from '@vakantiesnl/services/src/contentful';
import { getMinutesInMs } from '@vakantiesnl/services/src/util';
import { type ElementNavItem, type ContentfulLink } from '@vakantiesnl/types';

export type Metadata = {
	tags: Tag[];
};

export type Tag = {
	id: string;
	name: string;
};

export type Sys = {
	space: ContentfulLink;
	id: string;
	type: string;
	createdAt: string;
	updatedAt: string;
	environment: ContentfulLink;
	revision: number;
	contentType: ContentfulLink;
	locale: string;
};

export type NavItemLink = {
	fields: ContentfulLink;
	metadata: Metadata;
	sys: Sys;
};

type SharedElementNavItem = Omit<ElementNavItem, 'navItemType' | 'navigationItems'> & {
	navItemType?: ElementNavItem['navItemType'];
	navigationItems: Array<NavItemLink> | undefined;
};

export const useGetUserMenu = (): { userMenu: ElementNavItem | null } => {
	const menuTitle = 'User Menu';
	const query = useQuery({
		queryKey: [menuTitle],
		queryFn: () =>
			getEntryCollection('elementNavItem', {
				limit: 1,
				'fields.listTitle': menuTitle,
			}),
		staleTime: getMinutesInMs(60),
		placeholderData: keepPreviousData,
	});

	const userMenu = useMemo(() => {
		const rawUserMenu = query.data ? (query.data.items[0].fields as SharedElementNavItem) : null;

		//Remove sys and metadata from navigationItems to align the UserMenu with the ElementNaveItem type
		const filteredRawUserMenu = rawUserMenu
			? { ...rawUserMenu, navigationItems: rawUserMenu.navigationItems?.map((item) => item.fields) }
			: null;

		//The vakanties usermenu does not have a navItemType, so it is added in here
		return filteredRawUserMenu
			? filteredRawUserMenu?.navItemType
				? (filteredRawUserMenu as ElementNavItem)
				: ({ ...filteredRawUserMenu, navItemType: 'link' } as ElementNavItem)
			: null;
	}, [query.data]);

	return {
		userMenu: userMenu,
	};
};
